import { useQuery } from '@tanstack/react-query';

import { useOrpc } from './useOrpc';

interface UseRudderstackConfiguredReturn {
  /** True when the server reports a stored RudderStack link and a populated PAT. */
  isConfigured: boolean;
  /** True while the underlying settings.get query is in flight (and no cached data is available). */
  isLoading: boolean;
  /** True once settings.get has returned a payload. Use to gate UI that depends on a confirmed answer
   *  (so an in-flight or errored query doesn't render "not configured" by default). */
  hasSettings: boolean;
  /** Settings resolved and confirm no usable connection — shared "show the not-configured gate" signal. */
  isNotConfigured: boolean;
  /** The link's PAT is read-only: actions that change RudderStack are refused server-side (403). */
  isReadOnly: boolean;
}

/**
 * Single source of truth for whether the current workspace has a usable RudderStack connection.
 * Shared across Chat, Settings → General, and Settings → Sources so the three surfaces can't drift.
 */
export function useRudderstackConfigured(): UseRudderstackConfiguredReturn {
  const orpc = useOrpc();
  const { data, isLoading } = useQuery(orpc.settings.get.queryOptions({ input: {} }));
  const settings = data?.settings;
  // `rudderstackLinked` is the server's link rule: a shared key, or a sibling's organization (ACT2-1124). A sibling
  // workspace has no shared key, so a key check here would show it as not linked.
  const isConfigured = !!settings?.rudderstackLinked;
  const hasSettings = !!settings;
  return {
    isConfigured,
    isLoading,
    hasSettings,
    isNotConfigured: hasSettings && !isConfigured,
    isReadOnly: settings?.rudderstackReadOnly === true,
  };
}
